// 工作台工具栏 / Workbench toolbar.
// 管理 Demo/My Tasks/New Task、启动模拟和打开报告按钮的基础状态。
// Manages Demo/My Tasks/New Task and the base Start/Open Report button state.

import { ActiveReportGenerationError, ActiveSimulationError, RateLimitedError } from "@/api/client";
import { simulationsApi } from "@/api/simulations";
import { tasksApi as defaultTasksApi } from "@/api/tasks";
import { openAuthModal } from "@/features/auth-modal";
import { startButtonLoading } from "@/features/async-button";
import {
  renderCardList,
  renderCardListError,
  renderCardListSkeleton,
} from "@/features/card-list";
import { showInfoModal } from "@/features/info-modal";
import {
  currentBinding,
  subscribeBinding,
} from "@/features/log-terminal/simulation-binder";
import { showStopSimulationModal } from "@/features/stop-simulation-modal";
import type { Mode, TaskListItem } from "@/lib/types";
import { $maybe } from "@/lib/dom";
import { state } from "@/state/store";

interface TasksApiShape {
  list: (mode?: Mode) => Promise<{ ok: true; tasks: TaskListItem[] }>;
}

type WorkbenchTarget = { kind: "demo" | "task"; id: string; mode: Mode };
type ReportTarget = { kind: "demo" | "task"; id: string };

// 工具栏依赖注入 / Workbench toolbar dependency injection.
export interface WorkbenchToolbarDeps {
  tasksApi?: TasksApiShape;
  onCreateTask?: () => Promise<void> | void;
  onStartSimulation?: (target: WorkbenchTarget) => Promise<void> | void;
  onOpenReport?: (target: ReportTarget) => Promise<void> | void;
  windowOpen?: (url: string, target?: string) => void;
}

interface ResolvedWorkbenchToolbarDeps {
  tasksApi: TasksApiShape;
  onCreateTask?: () => Promise<void> | void;
  onStartSimulation?: (target: WorkbenchTarget) => Promise<void> | void;
  onOpenReport?: (target: ReportTarget) => Promise<void> | void;
  windowOpen: (url: string, target?: string) => void;
}

let toolbarDeps: ResolvedWorkbenchToolbarDeps;
let toolbarEvents: AbortController | null = null;
let taskListRequestSeq = 0;

const DEFAULT_DEPS: ResolvedWorkbenchToolbarDeps = {
  tasksApi: defaultTasksApi,
  windowOpen: (url, target) => {
    window.open(url, target);
  },
};

/**
 * 初始化工作台工具栏 / Initialize the workbench toolbar.
 */
export function initWorkbenchToolbar(deps: WorkbenchToolbarDeps = {}): void {
  toolbarDeps = { ...DEFAULT_DEPS, ...deps };
  toolbarEvents?.abort();
  toolbarEvents = new AbortController();
  const { signal } = toolbarEvents;

  $maybe<HTMLButtonElement>("view-demo-button")?.addEventListener("click", () => {
    invalidateTaskListRequests();
    state.workbenchView = "demo";
    renderCardList();
    refreshWorkbenchToolbar();
  }, { signal });

  $maybe<HTMLButtonElement>("view-tasks-button")?.addEventListener("click", (event) => {
    void loadMyTasks(event.currentTarget as HTMLButtonElement);
  }, { signal });

  $maybe<HTMLButtonElement>("new-task-button")?.addEventListener("click", (event) => {
    void createTask(event.currentTarget as HTMLButtonElement);
  }, { signal });

  $maybe<HTMLButtonElement>("start-simulation-button")?.addEventListener("click", (event) => {
    void startSelectedSimulation(event.currentTarget as HTMLButtonElement);
  }, { signal });

  $maybe<HTMLButtonElement>("stop-simulation-button")?.addEventListener("click", (event) => {
    void stopActiveSimulation(event.currentTarget as HTMLButtonElement);
  }, { signal });

  $maybe<HTMLButtonElement>("open-report-button")?.addEventListener("click", (event) => {
    void openSelectedReport(event.currentTarget as HTMLButtonElement);
  }, { signal });

  // binder 状态变化时同步刷新 Stop 按钮可用态。
  // Refresh the Stop button whenever the binder transitions.
  subscribeBinding(() => refreshWorkbenchToolbar());

  window.addEventListener("ripple:card-selection-changed", () => refreshWorkbenchToolbar(), {
    signal,
  });
  window.addEventListener("ripple:mode-changed", () => {
    if (state.workbenchView === "tasks" && isAuthenticated()) {
      // 切换模式会触发重新拉取任务列表 / Mode switch re-fetches tasks.
      // 立即画骨架，避免"卡片区先清空再等 fetch"的体感。
      // Paint the skeleton immediately so the grid is not cleared before fetch settles.
      renderCardListSkeleton("tasks");
      const button = $maybe<HTMLButtonElement>("view-tasks-button");
      if (button) void loadMyTasks(button);
      return;
    }
    renderCardList();
    refreshWorkbenchToolbar();
  }, { signal });
  window.addEventListener("ripple:language-changed", () => {
    state.selectedTaskId = null;
    if (state.workbenchView === "tasks" && isAuthenticated()) {
      renderCardListSkeleton("tasks");
      const button = $maybe<HTMLButtonElement>("view-tasks-button");
      if (button) void loadMyTasks(button);
      return;
    }
    renderCardList();
    refreshWorkbenchToolbar();
  }, { signal });
  window.addEventListener("ripple:demos-loaded", () => refreshWorkbenchToolbar(), { signal });
  window.addEventListener("ripple:auth-updated", () => refreshWorkbenchToolbar(), { signal });

  refreshWorkbenchToolbar();
}

/**
 * 刷新工具栏按钮状态 / Refresh toolbar button states.
 */
export function refreshWorkbenchToolbar(): void {
  const demoButton = $maybe<HTMLButtonElement>("view-demo-button");
  const tasksButton = $maybe<HTMLButtonElement>("view-tasks-button");
  const authKnown = state.auth !== null;
  demoButton?.classList.toggle("active", state.workbenchView === "demo");
  demoButton?.setAttribute("aria-pressed", String(state.workbenchView === "demo"));
  if (tasksButton && !tasksButton.classList.contains("is-loading")) {
    tasksButton.classList.toggle("active", state.workbenchView === "tasks");
    tasksButton.setAttribute("aria-pressed", String(state.workbenchView === "tasks"));
    tasksButton.disabled = !authKnown;
  }

  const newTask = $maybe<HTMLButtonElement>("new-task-button");
  if (newTask && !newTask.classList.contains("is-loading")) {
    newTask.disabled = !authKnown || !toolbarDeps.onCreateTask;
  }

  const hasSelection = hasSelectedCard();
  const startAvailable = Boolean(selectedTarget() && toolbarDeps.onStartSimulation);
  const reportAvailable =
    state.workbenchView === "demo"
      ? hasSelectedReport()
      : hasSelectedReport() && Boolean(toolbarDeps.onOpenReport);
  const start = $maybe<HTMLButtonElement>("start-simulation-button");
  if (start && !start.classList.contains("is-loading")) {
    start.disabled = isReportGenerationBusy() || state.running || !authKnown || !hasSelection || !startAvailable;
  }

  // Stop 按钮仅在 binder 处于 bound_live 时可点；否则置灰。
  // Stop button is only live while the binder has an active bound_live simulation.
  const stop = $maybe<HTMLButtonElement>("stop-simulation-button");
  if (stop && !stop.classList.contains("is-loading")) {
    const live = currentBinding().kind === "bound_live";
    stop.disabled = !authKnown || !live;
  }

  const report = $maybe<HTMLButtonElement>("open-report-button");
  if (report && !report.classList.contains("is-loading")) {
    report.disabled = state.running || !reportAvailable;
  }
}

// 加载用户任务 / Load signed-in user tasks.
async function loadMyTasks(button: HTMLButtonElement): Promise<void> {
  if (state.auth === null) return;
  if (!isAuthenticated()) {
    openAuthModal("login");
    return;
  }
  const requestId = ++taskListRequestSeq;
  const requestedMode = state.mode;
  // 立即切视图并渲染骨架，避免"点击后 Tasks 视图空白等待"。
  // Flip the view and paint a skeleton immediately so the workspace is not
  // blank while the task-list fetch is in-flight.
  state.workbenchView = "tasks";
  renderCardListSkeleton("tasks");
  refreshWorkbenchToolbar();
  const restore = startButtonLoading(button, "Loading");
  try {
    const body = await toolbarDeps.tasksApi.list(requestedMode);
    if (requestId !== taskListRequestSeq || state.mode !== requestedMode) return;
    state.tasks = body.tasks;
    renderCardList();
  } catch (error) {
    if (requestId !== taskListRequestSeq) return;
    state.tasks = [];
    renderCardListError(`Tasks unavailable: ${formatError(error)}`);
  } finally {
    restore();
    refreshWorkbenchToolbar();
  }
}

// 新建任务入口 / New-task entrypoint.
async function createTask(button: HTMLButtonElement): Promise<void> {
  if (state.auth === null) return;
  if (!isAuthenticated()) {
    openAuthModal("login");
    return;
  }
  const restore = startButtonLoading(button, "Creating");
  try {
    await toolbarDeps.onCreateTask?.();
  } catch (error) {
    renderCardListError(`Task action failed: ${formatError(error)}`);
  } finally {
    restore();
    refreshWorkbenchToolbar();
  }
}

// 启动当前选中卡片 / Start the selected card.
async function startSelectedSimulation(button: HTMLButtonElement): Promise<void> {
  const target = selectedTarget();
  if (!target) return;
  if (state.auth === null) return;
  if (!isAuthenticated()) {
    openAuthModal("login");
    return;
  }
  const restore = startButtonLoading(button, "Starting");
  try {
    await toolbarDeps.onStartSimulation?.(target);
  } catch (error) {
    // 已有任务 / 频次限制是"用户应知情的普通提示"，用信息弹窗而不是卡片区红色错误条。
    // Active / rate-limited starts are informational — surface them in a modal, not as a red banner.
    if (error instanceof ActiveSimulationError) {
      void showInfoModal({
        title: "Simulation already running",
        message: "Another simulation is already running. Please wait for it to finish before starting a new one.",
        tone: "info",
      });
    } else if (error instanceof ActiveReportGenerationError) {
      void showInfoModal({
        title: "Report generation in progress",
        message: "A report is currently being generated. Please wait for it to finish before starting a new simulation.",
        tone: "info",
      });
    } else if (error instanceof RateLimitedError) {
      void showInfoModal({
        title: "Too many requests",
        message: error.message || "Please wait a moment and try again.",
        tone: "warn",
      });
    } else {
      renderCardListError(`Simulation start failed: ${formatError(error)}`);
    }
  } finally {
    restore();
    refreshWorkbenchToolbar();
  }
}

// 报告生成全局忙碌态 / Global report-generation busy state.
function isReportGenerationBusy(): boolean {
  return Boolean(state.reportGeneration) || currentBinding().kind === "bound_report";
}

// 终止当前活跃仿真 / Abort the currently-running simulation.
// 先弹确认，用户同意后调 /cancel；后端会先优雅 cancel，失败则强杀 session。
// Confirm first, then hit /cancel; backend attempts a cooperative cancel before
// falling back to force-stopping the AgentCore session.
async function stopActiveSimulation(button: HTMLButtonElement): Promise<void> {
  const binding = currentBinding();
  if (binding.kind !== "bound_live") return;
  const simulationId = binding.simulationId;
  const proceed = await showStopSimulationModal();
  if (!proceed) return;
  const restore = startButtonLoading(button, "Stopping");
  try {
    await simulationsApi.cancel(simulationId);
  } catch (error) {
    void showInfoModal({
      title: "Stop failed",
      message: error instanceof Error
        ? `Unable to stop the current simulation: ${error.message}`
        : "Unable to stop the current simulation. Please try again later.",
      tone: "warn",
    });
  } finally {
    restore();
    refreshWorkbenchToolbar();
  }
}

// 打开当前选中卡片报告 / Open the selected card report.
async function openSelectedReport(button: HTMLButtonElement): Promise<void> {
  const demo = selectedDemo();
  if (state.workbenchView === "demo" && demo?.report_slug) {
    toolbarDeps.windowOpen(`/demo-reports/${encodeURIComponent(demo.report_slug)}`, "_blank");
    return;
  }
  const task = selectedTask();
  if (!task || task.ready_report_count <= 0 || !toolbarDeps.onOpenReport) return;
  const restore = startButtonLoading(button, "Opening");
  try {
    await toolbarDeps.onOpenReport({ kind: "task", id: task.id });
  } catch (error) {
    renderCardListError(`Report unavailable: ${formatError(error)}`);
  } finally {
    restore();
    refreshWorkbenchToolbar();
  }
}

// 是否已登录 / Whether the current user is authenticated.
function isAuthenticated(): boolean {
  return state.auth?.authenticated === true;
}

// 是否有选中卡片 / Whether a selectable card is active.
function hasSelectedCard(): boolean {
  return state.workbenchView === "demo" ? Boolean(selectedDemo()) : Boolean(selectedTask());
}

// 是否有可打开报告 / Whether the selected card has an openable report.
function hasSelectedReport(): boolean {
  const demo = selectedDemo();
  if (state.workbenchView === "demo") return Boolean(demo?.report_slug);
  return (selectedTask()?.ready_report_count ?? 0) > 0;
}

// 当前选中 Demo / Current selected Demo.
function selectedDemo(): { id: string; report_slug?: string | null } | null {
  const id = state.mode === "ab" ? state.selectedDemo.ab : state.selectedDemo.single;
  if (!id) return null;
  const list = state.mode === "ab" ? state.demos.ab : state.demos.single;
  return list.find((item) => item.id === id) ?? null;
}

// 当前选中 Task / Current selected Task.
function selectedTask(): TaskListItem | null {
  if (!state.selectedTaskId) return null;
  return state.tasks.find((task) => task.id === state.selectedTaskId && task.mode === state.mode)
    ?? null;
}

// 当前选中的工作台目标 / Current selected workbench target.
function selectedTarget(): WorkbenchTarget | null {
  if (state.workbenchView === "demo") {
    const demo = selectedDemo();
    return demo ? { kind: "demo", id: demo.id, mode: state.mode } : null;
  }
  const task = selectedTask();
  return task ? { kind: "task", id: task.id, mode: task.mode } : null;
}

// 使进行中的任务列表请求过期 / Invalidate in-flight task-list requests.
function invalidateTaskListRequests(): void {
  taskListRequestSeq += 1;
}

// 格式化错误消息 / Format an unknown error into a message.
function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
